import React, { useEffect, useState } from 'react'
import { Box, Button, Collapse, IconButton, Menu, MenuItem, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { router, usePage } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

function LineItemRow({ li }) {
  const [open, setOpen] = useState(false)
  const hasDistributions = li.unit_distributions && li.unit_distributions.length > 0

  return (
    <>
      <TableRow data-testid={`line-item-row-${li.id}`}>
        <TableCell sx={{ width: 48, p: 0.5 }}>
          {hasDistributions && (
            <IconButton size="small" onClick={() => setOpen(!open)} data-testid={`expand-btn-${li.id}`}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{li.cost_category_name}</TableCell>
        <TableCell align="right">{li.distribution_key_name}</TableCell>
        <TableCell align="right"><FormattedCurrency value={li.amount} /></TableCell>
      </TableRow>
      {hasDistributions && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                  {li.unit_distributions.map((ud) => (
                    <TableRow key={ud.unit_number} data-testid={`distribution-${li.id}-${ud.unit_number}`}
                      sx={{ backgroundColor: 'background.subtle' }}>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {ud.unit_number}
                        {ud.unit_location && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {ud.unit_location}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: '20%', fontSize: '0.75rem' }}>{ud.attribute_value || ''}</TableCell>
                      <TableCell align="right" sx={{ width: '20%', fontSize: '0.75rem' }}><FormattedCurrency value={ud.amount} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default function Show({ property, budget_plan, line_items = [], unit_results = [], generated_documents = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const title = property.management_type === 'weg' ? 'Wirtschaftsplan' : 'Budgetplan'
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null)

  const hasPendingDocuments = generated_documents.some(
    (doc) => doc.status === 'pending' || doc.status === 'generating'
  )

  useEffect(() => {
    if (!hasPendingDocuments) return

    const interval = setInterval(() => {
      router.reload({ only: ['generated_documents'] })
    }, 3000)

    return () => clearInterval(interval)
  }, [hasPendingDocuments])

  const handleCalculate = () => {
    router.patch(`${basePath}/budget_plans/${budget_plan.id}/calculate`)
  }

  const handleApprove = () => {
    router.patch(`${basePath}/budget_plans/${budget_plan.id}/approve`)
  }

  const handleGeneratePdf = (documentType) => {
    setPdfMenuAnchor(null)
    router.post(`${basePath}/budget_plans/${budget_plan.id}/generate_wirtschaftsplan`, {
      document_type: documentType,
    })
  }

  return (
    <Box>
      <PageHeader
        title={budget_plan.name}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: `${title}e`, href: `${basePath}/budget_plans` },
          { label: budget_plan.name },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {budget_plan.status === 'draft' && (
              <>
                <Button variant="outlined" size="small" onClick={handleCalculate} data-testid="calculate-btn">
                  Berechnen
                </Button>
                <Button variant="contained" size="small" onClick={handleApprove} data-testid="approve-btn">
                  Genehmigen
                </Button>
                <Button variant="outlined" size="small" startIcon={<EditIcon />}
                  onClick={() => router.visit(`${basePath}/budget_plans/${budget_plan.id}/edit`)}
                  data-testid="edit-btn"
                >
                  Bearbeiten
                </Button>
              </>
            )}
            {budget_plan.status === 'approved' && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                  data-testid="pdf-menu-btn"
                >
                  PDF generieren
                </Button>
                <Menu
                  anchorEl={pdfMenuAnchor}
                  open={Boolean(pdfMenuAnchor)}
                  onClose={() => setPdfMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleGeneratePdf('gesamtwirtschaftsplan')} data-testid="pdf-gesamt">
                    Gesamtwirtschaftsplan
                  </MenuItem>
                  <MenuItem onClick={() => handleGeneratePdf('einzelwirtschaftsplan')} data-testid="pdf-einzel">
                    Einzelwirtschaftspläne (alle Einheiten)
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        }
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge type="budget_plan_status" value={budget_plan.status} />
          <Typography variant="body2" color="text.secondary">
            Geschäftsjahr: {budget_plan.fiscal_year_label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rundung: {budget_plan.rounding_mode}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            Gesamt: <FormattedCurrency value={budget_plan.total_amount} align="left" />
          </Typography>
        </Box>
      </Paper>

      {/* Line Items */}
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Kostenpositionen</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 600 }}>Kostenkategorie</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '20%' }} align="right">Verteilerschlüssel</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '20%' }} align="right">Jahresbetrag</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {line_items.map((li) => (
              <LineItemRow key={li.id} li={li} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Unit Results */}
      {unit_results.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Ergebnisse je Einheit</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Jahresbetrag</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monatsbetrag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unit_results.map((ur) => (
                  <TableRow key={ur.unit_id} data-testid={`unit-result-${ur.unit_number}`}>
                    <TableCell>
                      {ur.unit_number}
                      {ur.unit_location && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{ur.unit_location}</Typography>}
                    </TableCell>
                    <TableCell align="right"><FormattedCurrency value={ur.annual_amount} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}><FormattedCurrency value={ur.monthly_amount} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Generated Documents */}
      {generated_documents.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Generierte Dokumente</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Titel</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Erstellt</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generated_documents.map((doc) => (
                  <TableRow key={doc.id} data-testid={`generated-doc-${doc.id}`}>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>
                      <StatusBadge type="generated_document_status" value={doc.status} />
                    </TableCell>
                    <TableCell>{doc.generated_at ? new Date(doc.generated_at).toLocaleString('de-DE') : '—'}</TableCell>
                    <TableCell align="right">
                      {doc.download_url && (
                        <Button
                          variant="text"
                          size="small"
                          href={doc.download_url}
                          data-testid={`download-btn-${doc.id}`}
                        >
                          Download
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}
